import React, { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import qs from 'query-string'

import {
  Box,
  Card,
  CircularProgress,
  Typography,
  makeAppStyles,
} from '@percept/mui'

import { CheckCircleOutline, ErrorOutline } from '@percept/mui/icons'

import { useCompleteOAuthInvitation } from '@percept/hooks'
import { getBrandingPlatformName } from '@percept/constants'
import { AppBranding } from '@percept/types'


export type OAuthCallbackProps = {
  /* Optional callback fired with the invitation's branding once the BE
   * confirms the OAuth grant. Multi-tenant apps wire this to their theme
   * switcher so the success view renders in the recipient's brand;
   * single-tenant apps omit it. */
  onBrandingResolved?: (branding: AppBranding) => void
}


const useStyles = makeAppStyles( theme => ({
  card: {
    width: '100%',
    maxWidth: 520,
    padding: theme.spacing(5),
    textAlign: 'center',
  },
  statusIcon: {
    fontSize: '3rem',
    marginBottom: theme.spacing(2),
  },
  successIcon: {
    color: theme.palette.success && theme.palette.success.main || theme.palette.primary.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
}))


export const OAuthCallback = ({
  onBrandingResolved,
}: OAuthCallbackProps): JSX.Element => {

  const classes = useStyles()

  const location = useLocation()
  const params = qs.parse(location.search)
  const code = typeof params.code === 'string' ? params.code : null
  const state = typeof params.state === 'string' ? params.state : null

  const mutation = useCompleteOAuthInvitation()
  const fired = useRef(false)

  useEffect(() => {
    if( code && state && !fired.current ){
      fired.current = true
      mutation.mutate({ token: state, code, state })
    }
    // mutation intentionally excluded — its reference changes on every state
    // update which would cause this effect to re-run on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, state])

  useEffect(() => {
    if( mutation.data && mutation.data.branding && onBrandingResolved ){
      onBrandingResolved(mutation.data.branding)
    }
  }, [mutation.data, onBrandingResolved])

  if( !code || !state ){
    return (
      <Card className={classes.card}>
        <ErrorOutline className={`${classes.statusIcon} ${classes.errorIcon}`} />
        <Typography variant='h5' gutterBottom>
          Authorisation Incomplete
        </Typography>
        <Typography variant='body1' color='textSecondary'>
          We did not receive a valid response from the provider. Please re-open your invitation link and try again.
        </Typography>
      </Card>
    )
  }

  if( mutation.isLoading || mutation.isIdle ){
    return (
      <Card className={classes.card}>
        <Box textAlign='center'>
          <CircularProgress className={classes.statusIcon} size='3rem' />
          <Typography variant='h6' color='textPrimary'>
            Storing authorisation credentials...
          </Typography>
        </Box>
      </Card>
    )
  }

  if( mutation.isSuccess ){
    return (
      <Card className={classes.card}>
        <CheckCircleOutline className={`${classes.statusIcon} ${classes.successIcon}`} />
        <Typography variant='h5' gutterBottom>
          Authorisation Complete
        </Typography>
        <Typography variant='body1' color='textSecondary'>
          Thanks. Access has been granted on behalf of <strong>{mutation.data.org_unit_name}</strong>.
          You can safely close this page.
        </Typography>
      </Card>
    )
  }

  const rawDetail = (
    mutation.error
    && mutation.error.response
    && mutation.error.response.data
    && (mutation.error.response.data as { detail?: unknown }).detail
  )
  let errorMessage: string | null = null
  if( typeof rawDetail === 'string' ){
    errorMessage = rawDetail
  } else if(
    rawDetail
    && typeof rawDetail === 'object'
    && typeof (rawDetail as { message?: unknown }).message === 'string'
  ){
    errorMessage = (rawDetail as { message: string }).message
  }

  const platformName = getBrandingPlatformName(process.env.BRANDING as AppBranding)

  return (
    <Card className={classes.card}>
      <ErrorOutline className={`${classes.statusIcon} ${classes.errorIcon}`} />
      <Typography variant='h5' gutterBottom>
        Authorisation Failed
      </Typography>
      <Typography variant='body1' color='textSecondary'>
        {errorMessage || (
          `The authorisation could not be completed. Please contact your ${platformName} representative for a new invitation.`
        )}
      </Typography>
    </Card>
  )
}


export default OAuthCallback
